import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { UnknownAction } from 'redux';
import styled from '@emotion/styled';

import { breakpoints, colors, Divider, durations, Icon, opacities } from '../../../design-system';
import withCommonUI from '../../common/hocs/withCommonUI';
import { CmsCustomTypes } from '../../cms/types';
import { RootState } from '../../../store/rootReducer';
import { getOrderNumber, removeToken } from '../../payment/utils';
import { formatPrice, getPersistedData } from '../../common/utils';
import { loadCmsContent } from '../../cms/actions';
import { setFormValues } from '../../form/actions';
import { Forms } from '../../form/types';
import { heights } from '../../common/constants';
import { BrandBanner } from '../../OurBrand/components/BrandBanner';
import paths from '../../routing/paths';
import { CartItem } from './CartItem';
import { UspSection } from '../../home/components/UspSection';
import { setOrderSuccessData, updatePaypalECS } from '../actions';
import { orderSuccessDataInitialState, paypalECSInitialState } from '../state';
import { calculateDeliveryDates } from '../../shipping/utils';
import { ShippingTypes } from '../types';
import { LBL_GET_ESTIMATION_DATE } from '../../shipping/locale';

const StyledOrderSuccessContainer = styled.div`
  margin-top: -${heights.HEADER_MOBILE}px;

  @media (min-width: ${breakpoints.S}px) {
    margin-top: -${heights.HEADER_TABLET}px;
  }

  @media (min-width: ${breakpoints.M}px) {
    margin-top: -${heights.HEADER}px;
  }

  .order-success-sumup-container {
    display: flex;
    flex-direction: column;
    gap: 32px;
    align-items: center;
    text-align: center;
    max-width: ${breakpoints.XL}px;
    background-color: ${colors.ORDER_SUCCESS};
    padding: ${`${heights.HEADER_MOBILE + 32}px 16px 40px 16px`};

    @media (min-width: ${breakpoints.S}px) {
      padding: ${`${heights.HEADER_TABLET + 16}px 24px 24px 24px`};
    }

    @media (min-width: ${breakpoints.M}px) {
      padding: ${`${heights.HEADER}px 24px 24px 24px`};
    }
  }

  .order-success-title {
    margin: 32px 0 0 0;
    font-family: Ogg-text;
    font-size: 3.2rem;
    line-height: 110%;
    font-weight: 700;
    color: ${colors.WHITE};

    @media (min-width: ${breakpoints.S}px) {
      font-size: 4.8rem;
    }
  }

  .order-success-number,
  .order-success-email {
    margin: 0;
    font-size: 1.4rem;
    line-height: 150%;
    color: ${colors.BACKGROUND};

    .order-number,
    .order-email {
      font-weight: 700;
    }
  }

  .order-success-number {
    text-transform: uppercase;
  }

  .order-success-link-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    flex-wrap: no-wrap;
    gap: 16px;

    @media (min-width: ${breakpoints.S}px) {
      margin-bottom: 32px;
      flex-direction: row;
    }
  }

  .order-success-home-link,
  .order-success-order-tracking-link {
    display: flex;
    align-items: center;
    justify-content: center;
    text-transform: uppercase;
    font-size: 1.4rem;
    font-weight: 700;
    width: 280px;
    height: 44px;
    padding: 0 40px;
    border-radius: 8px;
    transition: opacity ${durations.FOCUS_DELAY}ms ease-in-out;

    @media (hover: hover) {
      :hover {
        cursor: pointer;
        opacity: ${opacities.HOVERED};
      }
    }
    a {
      text-decoration: none;
    }
  }

  .order-success-home-link {
    background-color: ${colors.WHITE};

    a {
      color: ${colors.BLACK};
    }
  }

  .order-success-order-tracking-link {
    backgound-color: transparent;
    border: 1px solid ${colors.WHITE};

    a {
      color: ${colors.WHITE};
    }
  }

  .order-success-content-container {
    margin: 24px auto;
    width: calc(100% - 32px);
    border-bottom: solid 1px ${colors.LIGHT};
    padding-bottom: 24px;

    @media (min-width: ${breakpoints.S}px) {
      margin: 64px auto;
      padding-bottom: 32px;
    }
    @media (min-width: ${breakpoints.M}px) {
      width: ${breakpoints.M}px;
    }
  }

  .order-success-payment-informations {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 16px;

    @media (min-width: ${breakpoints.S}px) {
      flex-direction: row;

      justify-content: space-between;
    }
  }

  .order-success-payment-info-header {
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 150%;
    margin: 0;
  }

  .order-success-payment-infos-text {
    margin: 0;
    font-size: 1.4rem;
    line-height: 150%;
    color: ${colors.MAIN_GREY};
  }

  .order-success-payment-infos-price {
    font-weight: 700;
    color: ${colors.BLACK};
  }

  .order-success-order-details-title {
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 150%;
    margin: 0 auto 24px auto;
    width: calc(100% - 32px);

    @media (min-width: ${breakpoints.M}px) {
      width: ${breakpoints.M}px;
    }
  }

  .order-success-basket-summary-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: auto;
    padding-bottom: 48px;

    @media (min-width: ${breakpoints.S}px) {
      flex-direction: row;
      width: calc(100% - 32px);
      gap: 80px;
    }

    @media (min-width: ${breakpoints.M}px) {
      width: ${breakpoints.M}px;
    }
  }

  .order-success-items-list {
    display: flex;
    flex-direction: column;
    width: 100%;
    list-style: none;
    padding: 0;

    @media (min-width: ${breakpoints.S}px) {
      width: calc(100% - 380px);
    }
  }

  .order-success-summary-block {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    padding: 32px 16px 0 16px;

    @media (min-width: ${breakpoints.S}px) {
      padding: 0;
      width: 300px;
      height: min-content;
      position: sticky;
      top: ${heights.HEADER_TABLET}px;
    }

    @media (min-width: ${breakpoints.M}px) {
      top: ${heights.HEADER}px;
    }
  }

  .order-success-summary-price-details {
    font-size: 1.4rem;
    line-height: 150%;
    color: ${colors.MAIN_GREY};
    display: flex;
    justify-content: space-between;

    p {
      margin: 0;
    }
  }

  .order-summary-total-price {
    color: ${colors.BLACK};
    font-weight: 700;
  }

  .order-success-retrun-policy-block {
    background-color: ${colors.BACKGROUND};
    margin-top: 8px;
    padding: 16px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .return-block {
    display: flex;
    gap: 4px;

    p {
      margin: 4px 0 0 0;
      font-size: 1.2rem;
      line-height: 150%;
      font-weight: 700;
    }
  }

  .return-content {
    font-size: 1.2rem;
    color: ${colors.MAIN_GREY};
    line-height: 150%;
    margin: 0;
  }

  .anchor-animated {
    color: ${colors.MAIN_GREY};
    line-height: 1.4rem;
  }

  .success-color {
    color: ${colors.MAIN_SUCCESS};
  }
`;

const StyledErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  min-height: 50vh;
  background-color: ${colors.BACKGROUND};
  margin-top: -${heights.HEADER_MOBILE}px;

  @media (min-width: ${breakpoints.S}px) {
    margin-top: -${heights.HEADER_TABLET}px;
  }

  @media (min-width: ${breakpoints.M}px) {
    margin-top: -${heights.HEADER}px;
  }

  .error-message-text-container {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    margin: 128px 16px 32px 16px;

    @media (min-width: ${breakpoints.S}px) {
      margin: 128px 32px 32px 32px;
    }

    @media (min-width: ${breakpoints.M}px) {
      margin: 128px 64px 32px 64px;
    }
  }

  .error-icon {
    display: inline-block;
    vertical-align: middle;
    margin-top: -4px;
    padding: 0 4px;
  }

  .error-message-text-container {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    margin: 128px 16px 32px 16px;

    @media (min-width: ${breakpoints.S}px) {
      margin: 128px 32px 32px 32px;
    }

    @media (min-width: ${breakpoints.M}px) {
      margin: 128px 64px 32px 64px;
    }
  }

  .error-icon {
    display: inline-block;
    vertical-align: middle;
    margin-top: -4px;
    padding: 0 4px;
  }

  .error-message {
    color: ${colors.ERROR};
    font-size: 1.6rem;
    margin-bottom: 32px;
  }

  .error-message-text {
    font-size: 1.6rem;
    line-height: 2.4rem;
    color: ${colors.BLACK};
    text-align: center;
  }

  .error-link-container {
    display: flex;
    align-items: center;
    justify-content: center;

    width: 280px;
    height: 44px;
    padding: 0 40px;
    margin-bottom: 32px;
    background-color: ${colors.BLACK};
    border-radius: 8px;
    transition: opacity ${durations.FOCUS_DELAY}ms ease-in-out;

    @media (hover: hover) {
      :hover {
        cursor: pointer;
        opacity: ${opacities.HOVERED};
      }
    }
    a {
      text-decoration: none;
      text-transform: uppercase;
      font-size: 1.4rem;
      font-weight: 700;
      color: ${colors.WHITE};
    }
  }

  .order-success-usp-container {
    display: flex;
    order: 2;
  }
`;

export const OrderSuccess = () => {
  const dispatch = useDispatch();

  const giftItemTextColor =
    useSelector((state: RootState) => state.cms.cart?.gift_text_color) ?? 'BLACK';
  const { usp, usp_main_title } = useSelector((state: RootState) => state.cms.usp) ?? [];
  const { body1: cmsBrandBannerData } =
    useSelector((state: RootState) => state.cms.ordersuccess) ?? {};
  const { brand_banner_position_on_page } = cmsBrandBannerData?.[0]?.primary ?? {};
  const { cart, deliveryAddress } =
    useSelector((state: RootState) => state.cart.orderSuccessData) ?? {};
  const { street, streetAdditional, postal, city } = deliveryAddress ?? {};
  const {
    items,
    originalBaseTotal,
    shippingAmount,
    total,
    totalCouponAmount,
    totalDiscount,
    shippingType,
    storeId,
    pickupStation,
    isDigital,
  } = cart ?? {};
  const { allStores } = useSelector((state: RootState) => state.stores) ?? {};
  const selectedStore = allStores?.find((store) => store.id === storeId);

  const { name: storeName, address: storeAddress } = selectedStore ?? {};
  const {
    line1: storeStreet,
    line2: storeStreetAdditional,
    postalCode: storePostalCode,
    city: storeCity,
  } = storeAddress ?? {};

  const {
    home_delivery_min_day,
    home_delivery_max_day,
    store_delivery_min_day,
    store_delivery_max_day,
    pickup_delivery_min_day,
    pickup_delivery_max_day,
  } = useSelector((state: RootState) => state.cms.delivery) ?? {};

  const user = useSelector((state: RootState) => state.account.user);
  const { isFetching } = useSelector((state: RootState) => state.account);
  const name = isFetching ? '' : (user?.firstName ?? '');
  const email = isFetching ? '' : (user?.email ?? '');
  const {
    data,
    successData,
    cart: cartPaypalyECS,
  } = useSelector((state: RootState) => state.cart.paypalECS);

  const [orderNumber, setOrderNumber] = useState('');

  const {
    line1,
    line2,
    postalCode: pickupPostalCode,
    city: pickupCity,
    name: pickupName,
  } = pickupStation ?? {};

  const { email: emailPaypalECS, orderNumber: orderNumberPaypalECS } = successData ?? {};
  const { given_name: namePaypalECS } = data?.authorizedEvent.payer.name ?? {};
  const {
    items: itemsPaypalECS,
    originalBaseTotal: originalBaseTotalPaypalECS,
    shippingAmount: shippingAmountPaypalECS,
    total: totalPaypalECS,
    totalCouponAmount: totalCouponAmountPaypalECS,
    totalDiscount: totalDiscountAmountPaypalECS,
    isDigital: isDigitalPaypalECS,
  } = cartPaypalyECS ?? [];
  const {
    street: streetPaypalECS,
    postalCode: postalCodePaypalECS,
    city: cityPaypalECS,
  } = data?.deliveryAddress ?? {};

  const isPaypalECS = orderNumberPaypalECS !== '';

  const { dateStart, dateEnd } = calculateDeliveryDates(
    isPaypalECS || shippingType === ShippingTypes.HOME
      ? ShippingTypes.HOME
      : shippingType === ShippingTypes.STORE
        ? ShippingTypes.STORE
        : ShippingTypes.PICKUP,
    home_delivery_min_day,
    home_delivery_max_day,
    store_delivery_min_day,
    store_delivery_max_day,
    pickup_delivery_min_day,
    pickup_delivery_max_day
  );

  const addressMap = {
    [ShippingTypes.STORE]: {
      name: storeName,
      street: storeStreet,
      additional: storeStreetAdditional,
      postal: storePostalCode,
      city: storeCity,
    },
    [ShippingTypes.PICKUP]: {
      name: pickupName,
      street: line1,
      additional: line2,
      postal: pickupPostalCode,
      city: pickupCity,
    },
    default: {
      name: null,
      street: street,
      additional: streetAdditional,
      postal: postal,
      city: city,
    },
  };

  const {
    name: nameForPickupOrStore,
    street: streetForClassicalCheckout,
    additional: streetAdditionalForClassicalCheckout,
    postal: postalCodeForClassicalCheckout,
    city: cityForClassicalCheckout,
  } = shippingType && addressMap[shippingType] ? addressMap[shippingType] : addressMap.default;

  const getDeliverMethod = () => {
    if (isDigital || isDigitalPaypalECS) {
      return 'Livraison en ligne';
    } else if (isPaypalECS || shippingType === ShippingTypes.HOME) {
      return 'Livraison à domicile';
    } else if (shippingType === ShippingTypes.PICKUP) {
      return 'Livraison au point relais';
    }
    return 'Livraison en boutique';
  };

  const isAddressEmptyForPaypalECS = () => {
    return (
      Object.values(data?.billingAddress).every((value) => value === '') &&
      Object.values(data?.deliveryAddress).every((value) => value === '')
    );
  };

  useEffect(() => {
    dispatch(loadCmsContent(CmsCustomTypes.ordersuccess) as unknown as UnknownAction);
    dispatch(loadCmsContent(CmsCustomTypes.usp) as unknown as UnknownAction);
    dispatch(loadCmsContent(CmsCustomTypes.delivery) as unknown as UnknownAction);
    const token = getPersistedData('paymentToken');
    setOrderNumber(isPaypalECS ? orderNumberPaypalECS : getOrderNumber(token));
    // timeout needed in case we have one more cart opened on a different tab
    // to make sure the second tab is synchronized before removing the token
    setTimeout(() => removeToken(token), 3000);

    dispatch(
      setFormValues({
        form: Forms.delivery,
        values: {
          showBilling: false,
          showGiftMessage: false,
          isConditionsAccepted: false,
          pickupPhone: '',
          giftMessage: '',
        },
      })
    );

    return () => {
      if (isPaypalECS) {
        dispatch(updatePaypalECS(paypalECSInitialState));
      } else {
        dispatch(setOrderSuccessData(orderSuccessDataInitialState));
      }
    };
  }, []);

  if (!orderNumber || (isPaypalECS && isAddressEmptyForPaypalECS())) {
    return (
      <StyledErrorContainer>
        <div className="error-message-text-container">
          <p className="error-message-text">
            {`Chère cliente Darjeeling, votre commande est bien passée. Cependant une erreur interne est survenue, vous recevrez votre email de confirmation de commande d'ici quelques instants. Vous verrez votre commande apparaître dans votre compte client d'ici plusieurs minutes. Vous la trouverez dans votre compte client (icône utilisateur`}
            <span
              className="error-icon"
              style={{ display: 'inline-block', verticalAlign: 'middle' }}
            >
              <Icon name="account" size={16} />
            </span>
            {`en haut à droite de l'écran) rubrique "Vos commandes". Merci`}
          </p>
        </div>
        <p className="error-message">une erreur interne est survenue</p>
        <div className="error-link-container">
          <Link
            data-testid="order-error-link-to-home-page"
            to={paths.HOME}
            aria-label="Lien vers la page d'acceuil"
          >
            {`retour à la page d'accueil`}
          </Link>
        </div>
        {brand_banner_position_on_page === '1' && (
          <BrandBanner cmsData={cmsBrandBannerData[0]} index={0} />
        )}
        {usp?.length && (
          <div className="order-success-usp-container">
            <UspSection content={usp} title={usp_main_title ?? ''} />
          </div>
        )}
      </StyledErrorContainer>
    );
  }

  return (
    <StyledOrderSuccessContainer>
      <div className="order-success-sumup-container">
        <h2 className="order-success-title">{`Merci pour votre commande, ${isPaypalECS ? namePaypalECS : name}`}</h2>
        <div>
          <p className="order-success-number">
            Numéro de commande <span className="order-number">{orderNumber}</span>
          </p>
          <p className="order-success-email">
            Un email de confirmation vous sera envoyé à{' '}
            <span className="order-email">{`${isPaypalECS ? emailPaypalECS : email}`}</span>
          </p>
        </div>
        <div className="order-success-link-container">
          <div className="order-success-home-link">
            <Link
              data-testid="order-success-link-to-home-page"
              to={paths.HOME}
              aria-label="Lien vers la page d'acceuil"
            >
              {`retour à la page d'accueil`}
            </Link>
          </div>
          <div className="order-success-order-tracking-link">
            <Link
              data-testid="order-success-link-to-order-tracking"
              to={paths.ORDER_TRACKING}
              aria-label="Lien vers la page de suivi des commandes"
            >
              suivre votre commande
            </Link>
          </div>
        </div>
      </div>
      <div className="order-success-content-container">
        <div className="order-success-payment-informations">
          <div>
            <p className="order-success-payment-info-header">Adresse de livraison</p>
            {isDigital || isDigitalPaypalECS ? (
              <>
                <p className="order-success-payment-infos-text">
                  Votre commande sera livrée à votre adresse email
                </p>
                <p className="order-success-payment-infos-text">
                  {isPaypalECS ? emailPaypalECS : email}
                </p>
              </>
            ) : (
              <>
                {nameForPickupOrStore && (
                  <p className="order-success-payment-infos-text">{nameForPickupOrStore}</p>
                )}
                <p className="order-success-payment-infos-text">
                  {isPaypalECS ? streetPaypalECS : streetForClassicalCheckout}
                </p>
                {streetAdditionalForClassicalCheckout && (
                  <p className="order-success-payment-infos-text">
                    {streetAdditionalForClassicalCheckout}
                  </p>
                )}
                <p className="order-success-payment-infos-text">
                  {isPaypalECS
                    ? `${postalCodePaypalECS} ${cityPaypalECS}`
                    : `${postalCodeForClassicalCheckout} ${cityForClassicalCheckout}`}
                </p>
                <p className="order-success-payment-infos-text">France</p>
              </>
            )}
          </div>
          <div>
            <p className="order-success-payment-info-header">Mode de livraison</p>
            <p className="order-success-payment-infos-text">{getDeliverMethod()}</p>
            {(shippingAmountPaypalECS > 0 || shippingAmount > 0) && (
              <p className="order-success-payment-infos-text">
                {formatPrice(isPaypalECS ? shippingAmountPaypalECS : shippingAmount)}
              </p>
            )}
            {!(isDigital || isDigitalPaypalECS) && (
              <p className="order-success-payment-infos-text">
                {LBL_GET_ESTIMATION_DATE(dateStart, dateEnd)}
              </p>
            )}
          </div>
          <div>
            <p className="order-success-payment-info-header">Total de la commande</p>
            <p className="order-success-payment-infos-text order-success-payment-infos-price">
              {formatPrice(isPaypalECS ? totalPaypalECS : total)}
            </p>
          </div>
        </div>
      </div>
      <p className="order-success-order-details-title" id="order-success-order-details-title">
        Détails de la commande
      </p>
      <div className="order-success-basket-summary-container">
        <ul
          className="order-success-items-list"
          aria-labelledby="order-success-order-details-title"
        >
          {(isPaypalECS ? itemsPaypalECS : items)?.map((item) => {
            return (
              <li key={`${item.productRef}-${item.colorRef}`}>
                <CartItem
                  id={`${item.productRef}-${item.colorRef}`}
                  item={item}
                  giftItemTextColor={giftItemTextColor}
                  isOnlyView
                  isAvailable
                />
                <Divider color={colors.LIGHT} aria-hidden="true" />
              </li>
            );
          })}
        </ul>
        <div className="order-success-summary-block">
          <div className="order-success-summary-price-details">
            <p>Sous-total</p>
            <p>{formatPrice(isPaypalECS ? originalBaseTotalPaypalECS : originalBaseTotal)}</p>
          </div>
          {(totalDiscountAmountPaypalECS > 0 || totalDiscount > 0) && (
            <div className="order-success-summary-price-details success-color">
              <p>Remise appliquée</p>
              <p>-{formatPrice(isPaypalECS ? totalDiscountAmountPaypalECS : totalDiscount)}</p>
            </div>
          )}
          {(totalCouponAmountPaypalECS > 0 || totalCouponAmount > 0) && (
            <div className="order-success-summary-price-details success-color">
              <p>Code promo</p>
              <p>-{formatPrice(isPaypalECS ? totalCouponAmountPaypalECS : totalCouponAmount)}</p>
            </div>
          )}
          <div className="order-success-summary-price-details">
            <p>Livraison</p>
            <p>{formatPrice(isPaypalECS ? shippingAmountPaypalECS : shippingAmount)}</p>
          </div>
          <Divider />
          <div className="order-success-summary-price-details">
            <p className="order-summary-total-price">Total</p>
            <p className="order-summary-total-price">
              {formatPrice(isPaypalECS ? totalPaypalECS : total)}
            </p>
          </div>
          <div className="order-success-retrun-policy-block">
            <div className="return-block">
              <Icon name="return" size={24} />
              <p>Notre politique de retour</p>
            </div>
            <p className="return-content">
              Vous disposez de 30 jours pour faire un retour ou échanger vos produits.{' '}
              <a
                data-testid="Link-to-darjeeling-return-page"
                className="anchor-animated"
                href="https://faq.darjeeling.fr/hc/fr/sections/16814073258268-Retours-Echanges"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Lien vers la page des retours et échanges Darjeeling"
              >
                En savoir plus.
              </a>
            </p>
          </div>
        </div>
      </div>
      {brand_banner_position_on_page === '1' && (
        <BrandBanner cmsData={cmsBrandBannerData[0]} index={0} />
      )}
      {usp?.length && <UspSection content={usp} title={usp_main_title ?? ''} />}
    </StyledOrderSuccessContainer>
  );
};

export default withCommonUI(OrderSuccess);
